import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../../lib/navigation';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  User, 
  TrendingUp, 
  ArrowLeft,
  Eye,
  EyeOff,
  Plus,
  Info,
  AlertCircle,
  Shield,
  UserPlus
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import {
  CONTRACT_VALUE_BADGE_CONFIG,
  computeValueLabelFromProjectedProduction,
  ValueLabel,
} from '../../lib/contractValue';
import { 
  searchPlayersAdvanced, 
  addToWatchlist, 
  removeFromWatchlist, 
  getWatchlistPlayerIds
} from '../../lib/supabase';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';

interface AdvancedSearchResult {
  playerid: number;
  fullname: string;
  is_goalie: boolean;
  positioncode: string;
  nhl_team_abbrev: string;
  salary: number;
  goals: number;
  assists: number;
  points: number;
  gamesplayed?: number;
  // Stats de gardien
  wins?: number;
  losses?: number;
  shutouts?: number;
  savepct?: number;
  goalsagainstaverage?: number;
  // Autres
  age: number | null;
  contract_years_left: number | null;
  owner_user_id: number | null;
  owner_username: string | null;
  is_injured: boolean;
  is_scratch: boolean;
  is_tradingbloc: boolean;
  is_protected: boolean;
  created_at: string;
}

// Pattern TradingBloc: fonction utilitaire pour les photos de joueurs
const getPlayerImageUrl = (player: { nhl_team_abbrev: string; playerid: number }) => {
  return `https://assets.nhle.com/mugs/nhl/20242025/${player.nhl_team_abbrev}/${player.playerid}.png`;
};

const NHL_TEAMS = [
  'ANA', 'UTA', 'BOS', 'BUF', 'CAR', 'CBJ', 'CGY', 'CHI', 'COL', 'DAL',
  'DET', 'EDM', 'FLA', 'LAK', 'MIN', 'MTL', 'NSH', 'NJD', 'NYI', 'NYR',
  'OTT', 'PHI', 'PIT', 'SEA', 'SJS', 'STL', 'TBL', 'TOR', 'VAN', 'VGK',
  'WSH', 'WPG',
];

const POSITIONS = ['C', 'L', 'R', 'D', 'G'];
const RENTABILITY_OPTIONS: Array<{ id: ValueLabel; label: string }> = [
  { id: 'underpaid', label: 'Sous-payé' },
  { id: 'fair', label: 'Juste valeur' },
  { id: 'overpaid', label: 'Sur-payé' },
  { id: 'unknown', label: 'Données insuffisantes' },
];
const STATUSES = [
  { id: 'AGENT_LIBRE', label: 'Agent Libre' },
  { id: 'NHL', label: 'NHL (Main)' },
  { id: 'RESERVE', label: 'Réserve' },
  { id: 'INJURED', label: 'Blessé' },
  { id: 'SCRATCH', label: 'Scratch' }
];

export function AdvancedPlayerSearch() {
  usePageTracking('advanced_player_search');
  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUser = useUserStore((state) => state.user_id);
  const { language } = useLanguageStore();
  const { isStandardFlexible, isLoading: modeLoading } = useLeagueMode();

  // Search & Filter State
  const [term, setTerm] = useState('');
  const [minSalary, setMinSalary] = useState<string>('');
  const [maxSalary, setMaxSalary] = useState<string>('');
  const [minPoints, setMinPoints] = useState<string>('');
  const [maxPoints, setMaxPoints] = useState<string>('');
  const [minGoals, setMinGoals] = useState<string>('');
  const [maxGoals, setMaxGoals] = useState<string>('');
  const [minAssists, setMinAssists] = useState<string>('');
  const [maxAssists, setMaxAssists] = useState<string>('');
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedRentability, setSelectedRentability] = useState<ValueLabel[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState('points_desc');
  const [showTeamsDropdown, setShowTeamsDropdown] = useState(false);
  
  // Results State
  const [results, setResults] = useState<AdvancedSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [watchlistIds, setWatchlistIds] = useState<number[]>([]);
  const [expandedPlayerId, setExpandedPlayerId] = useState<number | null>(null);
  const [imageErrors, setImageErrors] = useState<{[key: number]: boolean}>({});

  const handleImageError = (playerId: number) => {
    setImageErrors(prev => ({ ...prev, [playerId]: true }));
  };

  const dedupePlayersById = (players: AdvancedSearchResult[]) => {
    const seen = new Set<number>();

    return players.filter((player) => {
      if (seen.has(player.playerid)) return false;
      seen.add(player.playerid);
      return true;
    });
  };

  const loadWatchlist = useCallback(async () => {
    if (!selectedLeague || !currentUser) return;
    const { data } = await getWatchlistPlayerIds(selectedLeague.id, Number(currentUser));
    if (data) setWatchlistIds(data);
  }, [selectedLeague, currentUser]);

  const handleSearch = useCallback(async () => {
    if (!selectedLeague) return;
    
    setLoading(true);
    try {
      // Convertir les millions en valeurs réelles (ex: 7.5 -> 7500000)
      const minSalaryValue = minSalary ? parseFloat(minSalary) * 1000000 : undefined;
      const maxSalaryValue = maxSalary ? parseFloat(maxSalary) * 1000000 : undefined;
      const effectiveLimit = selectedRentability.length > 0 ? 1000 : 100;
      const fetchLimit = isStandardFlexible && selectedRentability.length === 0
        ? 300
        : effectiveLimit;

      const { data, error } = await searchPlayersAdvanced({
        leagueId: selectedLeague.id,
        term: term.length >= 3 ? term : undefined,
        minSalary: minSalaryValue,
        maxSalary: maxSalaryValue,
        minPoints: minPoints ? parseInt(minPoints) : undefined,
        maxPoints: maxPoints ? parseInt(maxPoints) : undefined,
        minGoals: minGoals ? parseInt(minGoals) : undefined,
        maxGoals: maxGoals ? parseInt(maxGoals) : undefined,
        minAssists: minAssists ? parseInt(minAssists) : undefined,
        maxAssists: maxAssists ? parseInt(maxAssists) : undefined,
        positions: selectedPositions.length > 0 ? selectedPositions : undefined,
        statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        teamAbbrevs: selectedTeams.length > 0 ? selectedTeams : undefined,
        orderBy,
        // With rentability filtering, fetch a wider set first to avoid dropping valid overpaid/underpaid players.
        limit: fetchLimit
      });

      if (error) throw error;

      const rawResults = data || [];
      const filteredByRentability = selectedRentability.length > 0
        ? rawResults.filter((player: AdvancedSearchResult) => {
            const production = player.is_goalie
              ? (player.wins ?? 0)
              : (player.points ?? 0);
            const rentability = computeValueLabelFromProjectedProduction(
              player.salary,
              production,
              player.gamesplayed ?? 0,
              player.is_goalie
            );
            return selectedRentability.includes(rentability);
          })
        : rawResults;

      setResults(isStandardFlexible ? dedupePlayersById(filteredByRentability) : filteredByRentability);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  }, [selectedLeague, term, minSalary, maxSalary, minPoints, maxPoints, minGoals, maxGoals, minAssists, maxAssists, selectedPositions, selectedStatuses, selectedTeams, selectedRentability, orderBy, isStandardFlexible]);

  useEffect(() => {
    if (!selectedLeague || modeLoading) return;

    handleSearch();
    loadWatchlist();
  }, [selectedLeague, modeLoading, isStandardFlexible, orderBy, selectedPositions, selectedStatuses, selectedTeams, selectedRentability]);

  // Réinitialiser le tri quand on passe de gardiens à joueurs (ou vice versa)
  useEffect(() => {
    const isGoalieOnly = selectedPositions.length === 1 && selectedPositions.includes('G');
    const isGoalieSort = ['wins_desc', 'losses_asc', 'shutouts_desc'].includes(orderBy);
    const isPlayerSort = ['points_desc', 'goals_desc'].includes(orderBy);
    
    // Si on filtre gardiens mais qu'on a un tri de joueur, passer au tri par victoires
    if (isGoalieOnly && isPlayerSort) {
      setOrderBy('wins_desc');
    }
    // Si on filtre joueurs mais qu'on a un tri de gardien, passer au tri par points
    else if (!isGoalieOnly && selectedPositions.length > 0 && isGoalieSort) {
      setOrderBy('points_desc');
    }
  }, [selectedPositions]);

  const togglePosition = (pos: string) => {
    setSelectedPositions(prev => 
      prev.includes(pos) ? prev.filter(p => p !== pos) : [...prev, pos]
    );
  };

  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const toggleRentability = (value: ValueLabel) => {
    setSelectedRentability(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const toggleTeam = (team: string) => {
    setSelectedTeams(prev => 
      prev.includes(team) ? prev.filter(t => t !== team) : [...prev, team]
    );
  };

  const handleWatchlist = async (player: AdvancedSearchResult) => {
    if (!selectedLeague || !currentUser) return;

    const isWatched = watchlistIds.includes(player.playerid);
    try {
      if (isWatched) {
        await removeFromWatchlist(selectedLeague.id, Number(currentUser), player.playerid);
        setWatchlistIds(prev => prev.filter(id => id !== player.playerid));
        toast.success(translate('watchlist.removed.success', language, { playerName: player.fullname }));
      } else {
        await addToWatchlist(
          selectedLeague.id,
          Number(currentUser),
          player.playerid,
          player.fullname,
          player.positioncode,
          player.is_goalie,
          player.salary,
          player.nhl_team_abbrev
        );
        setWatchlistIds(prev => [...prev, player.playerid]);
        toast.success(translate('watchlist.added.success', language, { playerName: player.fullname }));
      }
    } catch (error) {
      toast.error('Erreur watchlist');
    }
  };

  const getRentabilityLabel = (player: AdvancedSearchResult): ValueLabel => {
    const production = player.is_goalie
      ? (player.wins ?? 0)
      : (player.points ?? 0);

    return computeValueLabelFromProjectedProduction(
      player.salary,
      production,
      player.gamesplayed ?? 0,
      player.is_goalie
    );
  };

  const resetFilters = () => {
    setTerm('');
    setMinSalary('');
    setMaxSalary('');
    setMinPoints('');
    setMaxPoints('');
    setMinGoals('');
    setMaxGoals('');
    setMinAssists('');
    setMaxAssists('');
    setSelectedPositions([]);
    setSelectedStatuses([]);
    setSelectedRentability([]);
    setSelectedTeams([]);
    setOrderBy('points_desc');
  };

  // Close teams dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // If click is outside any element inside the dropdown card, close it
      if (!target.closest || !document) return;
      const insideDropdown = !!target.closest('.card.position-absolute');
      const insideButton = !!target.closest('button');
      if (!insideDropdown && !insideButton) {
        setShowTeamsDropdown(false);
      }
    };

    if (showTeamsDropdown) {
      document.addEventListener('click', handler);
    }
    return () => {
      document.removeEventListener('click', handler);
    };
  }, [showTeamsDropdown]);

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
            <button 
              onClick={() => goBackOrHome(navigate)} 
              className="btn btn-outline-secondary btn-sm"
            >
            <ArrowLeft size={18} className="me-2" />
            Retour
          </button>
          <h1 className="h3 mb-0 fw-bold text-primary">
            <Search size={24} className="me-2 d-inline" />
            {translate('advanced.search.title', language)}
          </h1>
        </div>
        <button 
          onClick={() => setShowFilters(!showFilters)}
          className={`btn ${showFilters ? 'btn-primary' : 'btn-outline-primary'}`}
        >
          <Filter size={18} className="me-2" />
          {showFilters ? 'Masquer filtres' : 'Afficher filtres'}
        </button>
      </div>

      <div className="row g-4">
        {/* Filters Sidebar */}
        {showFilters && (
          <div className="col-lg-3">
            <div
              className="card shadow-sm sticky-top"
              style={{ top: '20px', zIndex: 10, overflow: 'visible', position: 'relative' }}
            >
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h5 className="card-title mb-0 d-flex align-items-center">
                    <Filter size={18} className="me-2 text-primary" />
                    {translate('advanced.search.filters', language)}
                  </h5>
                  <button 
                    onClick={resetFilters} 
                    className="btn btn-sm btn-outline-danger"
                  >
                    Réinitialiser
                  </button>
                </div>

                {/* Text Search */}
                <div className="mb-3">
                  <label className="form-label text-muted small fw-bold text-uppercase">
                    Nom du joueur
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <Search size={18} className="text-muted" />
                    </span>
                    <input 
                      type="text" 
                      placeholder="Rechercher..." 
                      className="form-control border-start-0"
                      value={term}
                      onChange={(e) => setTerm(e.target.value)}
                    />
                  </div>
                  {term.length > 0 && term.length < 3 && (
                    <small className="text-muted">Minimum 3 caractères</small>
                  )}
                </div>

                {/* Salary Range */}
                <div className="mb-3">
                  <label className="form-label text-muted small fw-bold text-uppercase">
                    Salaire (Millions $)
                  </label>
                  <div className="row g-2">
                    <div className="col-6">
                      <input 
                        type="number" 
                        className="form-control form-control-sm" 
                        placeholder="Min (ex: 1.5)"
                        step="0.1"
                        value={minSalary}
                        onChange={(e) => setMinSalary(e.target.value)}
                      />
                    </div>
                    <div className="col-6">
                      <input 
                        type="number" 
                        className="form-control form-control-sm" 
                        placeholder="Max (ex: 10.5)"
                        step="0.1"
                        value={maxSalary}
                        onChange={(e) => setMaxSalary(e.target.value)}
                      />
                    </div>
                  </div>
                  {(minSalary || maxSalary) && (
                    <small className="text-muted d-block mt-1">
                      {minSalary && `Min: ${parseFloat(minSalary).toFixed(1)}M$`}
                      {minSalary && maxSalary && ' | '}
                      {maxSalary && `Max: ${parseFloat(maxSalary).toFixed(1)}M$`}
                    </small>
                  )}
                </div>

                {/* Points Range */}
                <div className="mb-3">
                  <label className="form-label text-muted small fw-bold text-uppercase">
                    Points
                  </label>
                  <div className="row g-2">
                    <div className="col-6">
                      <input 
                        type="number" 
                        className="form-control form-control-sm" 
                        placeholder="Min"
                        value={minPoints}
                        onChange={(e) => setMinPoints(e.target.value)}
                      />
                    </div>
                    <div className="col-6">
                      <input 
                        type="number" 
                        className="form-control form-control-sm" 
                        placeholder="Max"
                        value={maxPoints}
                        onChange={(e) => setMaxPoints(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Goals Range */}
                <div className="mb-3">
                  <label className="form-label text-muted small fw-bold text-uppercase">
                    Buts
                  </label>
                  <div className="row g-2">
                    <div className="col-6">
                      <input 
                        type="number" 
                        className="form-control form-control-sm" 
                        placeholder="Min"
                        value={minGoals}
                        onChange={(e) => setMinGoals(e.target.value)}
                      />
                    </div>
                    <div className="col-6">
                      <input 
                        type="number" 
                        className="form-control form-control-sm" 
                        placeholder="Max"
                        value={maxGoals}
                        onChange={(e) => setMaxGoals(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Assists Range */}
                <div className="mb-3">
                  <label className="form-label text-muted small fw-bold text-uppercase">
                    Passes
                  </label>
                  <div className="row g-2">
                    <div className="col-6">
                      <input 
                        type="number" 
                        className="form-control form-control-sm" 
                        placeholder="Min"
                        value={minAssists}
                        onChange={(e) => setMinAssists(e.target.value)}
                      />
                    </div>
                    <div className="col-6">
                      <input 
                        type="number" 
                        className="form-control form-control-sm" 
                        placeholder="Max"
                        value={maxAssists}
                        onChange={(e) => setMaxAssists(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Positions */}
                <div className="mb-3">
                  <label className="form-label text-muted small fw-bold text-uppercase">
                    Positions
                  </label>
                  <div className="d-flex flex-wrap gap-2">
                    {POSITIONS.map(pos => (
                      <button
                        key={pos}
                        onClick={() => togglePosition(pos)}
                        className={`btn btn-sm ${selectedPositions.includes(pos) ? 'btn-primary' : 'btn-outline-secondary'}`}
                      >
                        {pos}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Statuses */}
                <div className="mb-3">
                  <label className="form-label text-muted small fw-bold text-uppercase">
                    Statuts
                  </label>
                  <div className="d-flex flex-wrap gap-2">
                    {STATUSES.map(status => (
                      <button
                        key={status.id}
                        onClick={() => toggleStatus(status.id)}
                        className={`btn btn-sm ${selectedStatuses.includes(status.id) ? 'btn-primary' : 'btn-outline-secondary'}`}
                      >
                        {status.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Rentability */}
                <div className="mb-3">
                  <label className="form-label text-muted small fw-bold text-uppercase">
                    Rentabiliter
                  </label>
                  <div className="d-flex flex-wrap gap-2">
                    {RENTABILITY_OPTIONS.map(option => (
                      <button
                        key={option.id}
                        onClick={() => toggleRentability(option.id)}
                        className={`btn btn-sm ${selectedRentability.includes(option.id) ? 'btn-primary' : 'btn-outline-secondary'}`}
                      >
                        {translate(`player.detail.contract.${option.id}`, language)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* NHL Teams - compact dropdown multi-select */}
                <div className="mb-4 position-relative">
                  <label className="form-label text-muted small fw-bold text-uppercase d-block mb-2">
                    Équipes NHL
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <div className="position-relative">
                      <button
                        type="button"
                        className={`btn btn-sm ${selectedTeams.length ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setShowTeamsDropdown(prev => !prev)}
                        aria-expanded={showTeamsDropdown}
                      >
                        {selectedTeams.length ? `${selectedTeams.length} sélectionnée(s)` : 'Sélectionner équipes'}
                        <span className="ms-2">▾</span>
                      </button>

                      {showTeamsDropdown && (
                        <div
                          className="card shadow-sm position-absolute"
                          style={{ zIndex: 60, width: '260px', top: '36px', left: 0 }}
                        >
                          <div className="card-body p-2">
                            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                              {NHL_TEAMS.map(team => (
                                <div key={team} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`team-${team}`}
                                    checked={selectedTeams.includes(team)}
                                    onChange={() => {
                                      toggleTeam(team);
                                    }}
                                  />
                                  <label className="form-check-label" htmlFor={`team-${team}`}>
                                    {team}
                                  </label>
                                </div>
                              ))}
                            </div>

                            <div className="d-flex justify-content-between align-items-center mt-2">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => { setSelectedTeams([]); setShowTeamsDropdown(false); }}
                              >
                                Effacer
                              </button>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => setShowTeamsDropdown(false)}
                              >
                                Appliquer
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      {selectedTeams.length > 0 && (
                        <small className="text-muted">{selectedTeams.join(', ')}</small>
                      )}
                    </div>
                  </div>
                </div>

                <button 
                  onClick={handleSearch} 
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Recherche...
                    </>
                  ) : (
                    <>
                      <Search size={18} className="me-2" />
                      {translate('advanced.search.apply.filters', language)}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Area */}
        <div className={showFilters ? 'col-lg-9' : 'col-12'}>
          {/* Results Header */}
          <div className="card shadow-sm mb-3">
            <div className="card-body py-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary fs-6">{results.length}</span>
                  <span className="text-muted">{translate('advanced.search.results', language)}</span>
                </div>
                <select 
                  className="form-select form-select-sm w-auto"
                  value={orderBy}
                  onChange={(e) => setOrderBy(e.target.value)}
                >
                  {/* Si on filtre uniquement les gardiens, afficher options de gardien */}
                  {selectedPositions.length === 1 && selectedPositions.includes('G') ? (
                    <>
                      <option value="wins_desc">Victoires (Décroissant)</option>
                      <option value="shutouts_desc">Blanchissages (Décroissant)</option>
                      <option value="losses_asc">Défaites (Croissant)</option>
                      <option value="salary_asc">Salaire (Croissant)</option>
                      <option value="salary_desc">Salaire (Décroissant)</option>
                      <option value="created_at_desc">Plus récents</option>
                    </>
                  ) : (
                    /* Options pour joueurs ou recherche mixte */
                    <>
                      <option value="points_desc">Points (Décroissant)</option>
                      <option value="goals_desc">Buts (Décroissant)</option>
                      <option value="salary_asc">Salaire (Croissant)</option>
                      <option value="salary_desc">Salaire (Décroissant)</option>
                      <option value="created_at_desc">Plus récents</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="card shadow-sm">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted">{translate('advanced.search.loading', language)}</p>
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="card shadow-sm">
              <div className="card-body text-center py-5">
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                  <Search size={40} className="text-muted opacity-25" />
                </div>
                <h5 className="fw-bold mb-2">Aucun résultat</h5>
                <p className="text-muted mb-3">
                  {translate('advanced.search.no.results', language)}
                </p>
                <button onClick={resetFilters} className="btn btn-outline-secondary btn-sm">
                  Réinitialiser les filtres
                </button>
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {results.map((player) => {
                const rentabilityLabel = getRentabilityLabel(player);
                const rentabilityBadge = CONTRACT_VALUE_BADGE_CONFIG[rentabilityLabel];
                const showLeagueOwnership = !isStandardFlexible;

                // Définir la couleur de bordure selon le statut (Pattern TradingBloc)
                const getBorderColor = () => {
                  if (!showLeagueOwnership) {
                    return player.owner_user_id ? '#0d6efd' : '#198754';
                  }
                  if (!player.owner_user_id) return '#198754'; // Vert pour agent libre
                  if (player.is_tradingbloc) return '#0dcaf0'; // Cyan pour bloc d'échange
                  if (player.is_protected) return '#ffc107'; // Or pour protégé
                  return '#0d6efd'; // Bleu pour possédé
                };

                return (
                  <div 
                    key={player.playerid}
                    className="card border-0 shadow-sm"
                  >
                    <div className="card-body">
                      {/* Layout responsive moderne avec photo de joueur */}
                      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
                        {/* Player Info avec photo circulaire (Pattern TradingBloc) */}
                        <div className="d-flex align-items-center gap-3 flex-grow-1">
                          {/* Photo circulaire avec bordure colorée */}
                          <div className="position-relative flex-shrink-0" onClick={() => navigate(`/players/${player.playerid}`)} style={{ cursor: 'pointer' }}>
                            <img
                              src={getPlayerImageUrl(player)}
                              alt={player.fullname}
                              className="rounded-circle"
                              style={{
                                width: '48px',
                                height: '48px',
                                objectFit: 'cover',
                                border: `3px solid ${getBorderColor()}`,
                                display: imageErrors[player.playerid] ? 'none' : 'block'
                              }}
                              onError={() => handleImageError(player.playerid)}
                            />
                            {imageErrors[player.playerid] && (
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                  width: '48px',
                                  height: '48px',
                                  backgroundColor: '#f8f9fa',
                                  border: `3px solid ${getBorderColor()}`
                                }}
                              >
                                <User size={24} className="text-muted" />
                              </div>
                            )}

                            {/* Badge blessé en overlay */}
                            {player.is_injured && (
                              <span
                                className="position-absolute top-0 end-0 translate-middle badge rounded-pill bg-danger"
                                style={{ fontSize: '0.6rem' }}
                              >
                                <AlertCircle size={10} />
                              </span>
                            )}
                          </div>

                          {/* Nom et infos */}
                          <div className="flex-grow-1 min-w-0">
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                              <h6 className="mb-0 fw-bold" onClick={() => navigate(`/players/${player.playerid}`)} style={{ cursor: 'pointer' }}>{player.fullname}</h6>
                              <span className="badge bg-primary">{player.positioncode}</span>
                              <span className={`badge ${rentabilityBadge.bg}`}>
                                {translate(rentabilityBadge.textKey, language)}
                              </span>
                              {showLeagueOwnership && player.is_protected && (
                                <span className="badge bg-warning text-dark" title="Joueur protégé">
                                  <Shield size={12} />
                                </span>
                              )}
                              {showLeagueOwnership && player.is_tradingbloc && (
                                <span className="badge bg-info" title="Disponible pour échange">
                                  <TrendingUp size={12} />
                                </span>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2 small flex-wrap">
                              {showLeagueOwnership && player.owner_username ? (
                                <span className="d-flex align-items-center gap-1 text-primary fw-semibold">
                                  <User size={14} />
                                  {player.owner_username}
                                </span>
                              ) : showLeagueOwnership ? (
                                <span className="text-success fw-semibold">
                                  <UserPlus size={14} className="me-1" />
                                  Agent Libre
                                </span>
                              ) : null}
                              {showLeagueOwnership && <span className="text-muted">•</span>}
                              <span className="fw-bold">{(player.salary / 1000000).toFixed(2)}M$</span>
                            </div>

                            {/* Stats MOBILE - affichées sous le nom/salaire */}
                            <div className="d-md-none mt-2">
                              {player.is_goalie ? (
                                <div className="d-flex gap-3 align-items-center">
                                  <div className="text-center">
                                    <div className="fw-bold text-muted">{player.gamesplayed || 0}</div>
                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>PJ</small>
                                  </div>
                                  <div className="text-center">
                                    <div className="fw-bold text-primary">{player.wins || 0}</div>
                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>V</small>
                                  </div>
                                  <div className="text-center">
                                    <div className="fw-bold">{player.losses || 0}</div>
                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>D</small>
                                  </div>
                                  <div className="text-center">
                                    <div className="fw-bold">{player.shutouts || 0}</div>
                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>BL</small>
                                  </div>
                                </div>
                              ) : (
                                <div className="d-flex gap-3 align-items-center">
                                  <div className="text-center">
                                    <div className="fw-bold text-muted">{player.gamesplayed || 0}</div>
                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>PJ</small>
                                  </div>
                                  <div className="text-center">
                                    <div className="fw-bold text-primary">{player.points}</div>
                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>PTS</small>
                                  </div>
                                  <div className="text-center">
                                    <div className="fw-bold">{player.goals}</div>
                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>B</small>
                                  </div>
                                  <div className="text-center">
                                    <div className="fw-bold">{player.assists}</div>
                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>P</small>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Stats & Actions - Desktop */}
                        <div className="d-none d-md-flex align-items-center gap-4">
                          {player.is_goalie ? (
                            <>
                              <div className="text-center d-none d-xl-block">
                                <div className="fs-4 fw-semibold text-muted">{player.gamesplayed || 0}</div>
                                <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>PJ</small>
                              </div>
                              <div className="text-center">
                                <div className="fs-3 fw-bold text-primary">{player.wins || 0}</div>
                                <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Victoires</small>
                              </div>
                              <div className="text-center d-none d-xl-block">
                                <div className="fs-4 fw-semibold">{player.losses || 0}</div>
                                <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Défaites</small>
                              </div>
                              <div className="text-center d-none d-xl-block">
                                <div className="fs-4 fw-semibold">{player.shutouts || 0}</div>
                                <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Blanchis</small>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-center d-none d-xl-block">
                                <div className="fs-4 fw-semibold text-muted">{player.gamesplayed || 0}</div>
                                <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>PJ</small>
                              </div>
                              <div className="text-center">
                                <div className="fs-3 fw-bold text-primary">{player.points}</div>
                                <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Points</small>
                              </div>
                              <div className="text-center d-none d-xl-block">
                                <div className="fs-4 fw-semibold">{player.goals}</div>
                                <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Buts</small>
                              </div>
                              <div className="text-center d-none d-xl-block">
                                <div className="fs-4 fw-semibold">{player.assists}</div>
                                <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Passes</small>
                              </div>
                            </>
                          )}

                          {/* Actions buttons - Desktop */}
                          <div className="d-flex gap-2">
                            <button 
                              onClick={() => handleWatchlist(player)}
                              className={`btn btn-sm ${watchlistIds.includes(player.playerid) ? 'btn-primary' : 'btn-outline-secondary'}`}
                              title={watchlistIds.includes(player.playerid) ? 'Retirer de la watchlist' : 'Ajouter à la watchlist'}
                            >
                              {watchlistIds.includes(player.playerid) ? <Eye size={16} /> : <EyeOff size={16} />}
                            </button>
                            <button 
                              onClick={() => setExpandedPlayerId(expandedPlayerId === player.playerid ? null : player.playerid)}
                              className="btn btn-sm btn-outline-secondary"
                            >
                              {expandedPlayerId === player.playerid ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                          </div>
                        </div>

                        {/* Actions buttons - Mobile uniquement */}
                        <div className="d-md-none d-flex gap-2 w-100">
                          <button 
                            onClick={() => handleWatchlist(player)}
                            className={`btn btn-sm flex-fill ${watchlistIds.includes(player.playerid) ? 'btn-primary' : 'btn-outline-secondary'}`}
                          >
                            {watchlistIds.includes(player.playerid) ? (
                              <><Eye size={16} className="me-1" /> Watchlist</>
                            ) : (
                              <><EyeOff size={16} className="me-1" /> Ajouter</>
                            )}
                          </button>
                          <button 
                            onClick={() => setExpandedPlayerId(expandedPlayerId === player.playerid ? null : player.playerid)}
                            className="btn btn-sm btn-outline-secondary"
                          >
                            {expandedPlayerId === player.playerid ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedPlayerId === player.playerid && (
                        <div className="mt-3 pt-3 border-top">
                          <div className="row g-2 mb-3">
                            {player.is_goalie ? (
                              // Stats détaillées pour gardien
                              <>
                                <div className="col-6 col-md-4 col-lg-3">
                                  <div className="bg-light rounded p-3 text-center">
                                    <small className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Parties jouées</small>
                                    <div className="fs-4 fw-bold text-muted">{player.gamesplayed || 0}</div>
                                  </div>
                                </div>
                                <div className="col-6 col-md-4 col-lg-3">
                                  <div className="bg-primary bg-opacity-10 rounded p-3 text-center border border-primary border-opacity-25">
                                    <small className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Victoires</small>
                                    <div className="fs-4 fw-bold text-primary">{player.wins || 0}</div>
                                  </div>
                                </div>
                                <div className="col-6 col-md-4 col-lg-3">
                                  <div className="bg-light rounded p-3 text-center">
                                    <small className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Défaites</small>
                                    <div className="fs-4 fw-bold">{player.losses || 0}</div>
                                  </div>
                                </div>
                                <div className="col-6 col-md-4 col-lg-3">
                                  <div className="bg-light rounded p-3 text-center">
                                    <small className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Blanchis</small>
                                    <div className="fs-4 fw-bold">{player.shutouts || 0}</div>
                                  </div>
                                </div>
                                <div className="col-6 col-md-4 col-lg-3">
                                  <div className="bg-light rounded p-3 text-center">
                                    <small className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>% Arrêts</small>
                                    <div className="fs-4 fw-bold">
                                      {player.savepct ? (player.savepct * 100).toFixed(1) + '%' : '-'}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-6 col-md-4 col-lg-3">
                                  <div className="bg-light rounded p-3 text-center">
                                    <small className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>MOY</small>
                                    <div className="fs-4 fw-bold">
                                      {player.goalsagainstaverage ? player.goalsagainstaverage.toFixed(2) : '-'}
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              // Stats détaillées pour joueur
                              <>
                                <div className="col-6 col-md-3">
                                  <div className="bg-light rounded p-3 text-center">
                                    <small className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Parties jouées</small>
                                    <div className="fs-4 fw-bold text-muted">{player.gamesplayed || 0}</div>
                                  </div>
                                </div>
                                <div className="col-6 col-md-3">
                                  <div className="bg-primary bg-opacity-10 rounded p-3 text-center border border-primary border-opacity-25">
                                    <small className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Buts</small>
                                    <div className="fs-4 fw-bold text-primary">{player.goals}</div>
                                  </div>
                                </div>
                                <div className="col-6 col-md-3">
                                  <div className="bg-light rounded p-3 text-center">
                                    <small className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Passes</small>
                                    <div className="fs-4 fw-bold">{player.assists}</div>
                                  </div>
                                </div>
                              </>
                            )}
                            <div className="col-6 col-md-3">
                              <div className="bg-light rounded p-3 text-center">
                                <small className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Âge</small>
                                <div className="fs-4 fw-bold">{player.age || '?'}</div>
                              </div>
                            </div>
                          </div>

                          <div className="d-flex flex-wrap gap-2">
                            <button 
                              onClick={() => navigate(`/players/${player.playerid}`)}
                              className="btn btn-outline-primary btn-sm flex-fill"
                            >
                              <Info size={16} className="me-1" />
                              Profil Complet
                            </button>
                            {!isStandardFlexible && !player.owner_user_id && (
                              <button 
                                onClick={() => navigate('/free-agents', { state: { selectedPlayerId: player.playerid } })}
                                className="btn btn-success btn-sm flex-fill"
                              >
                                <Plus size={16} className="me-1" />
                                Signer
                              </button>
                            )}
                            {!isStandardFlexible && player.owner_user_id && player.owner_user_id !== Number(currentUser || 0) && (
                              <button 
                                onClick={() => navigate('/trade-player', { state: { targetUserId: player.owner_user_id, targetPlayerId: player.playerid } })}
                                className="btn btn-primary btn-sm flex-fill"
                              >
                                <TrendingUp size={16} className="me-1" />
                                Échanger
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
